/**
 * V108-6 setup screens — the pure model behind the coverage view (onboarding step 8), the Setup page
 * and Today's program cards + Next step strip (design pass 2026-10-11, docs/v10.8/setup-screens-design.md).
 *
 * ONE count source: every screen derives "N of M seen · K to go", the four-part bar, the projection and
 * the next step from the same two server reads (GET /coverage + GET /enablers). When a step reaches
 * Done the server recomputes both, so every number moves together.
 *
 * Nothing here invents data the server does not have:
 *   - the weekly delta and the pace come from the coverage snapshots (`trend`); with less than a week of
 *     history they are omitted, not guessed;
 *   - there is no failing count per program yet, so the status popover links to the program instead;
 *   - asks have no sent / opened tracking: "Remind" copies a reminder to paste, it does not claim a send.
 */
import { failureCauseView, vendorName, type FailureCauseView } from '@aigovops/core';
import type { CoverageProgram, EnablerItem } from './setup-api';

// ---------------------------------------------------------------------------------------------------
// Coverage numbers (the four-part bar)
// ---------------------------------------------------------------------------------------------------

export interface CoverageParts {
  program: string;
  label: string;
  total: number;
  seen: number;
  togo: number;
  /** The program's next step (first open step that unlocks one of its objectives) and its share. */
  next: { step: EnablerItem; count: number } | null;
  other: number;
  noSource: number;
  preview: number;
  partial: number;
}

/** Open = something a person can start now (not done / declined / snoozed / blocked). */
export const isOpenStep = (e: EnablerItem) => e.state === 'suggested' || e.state === 'in_progress';
const byRank = (a: EnablerItem, b: EnablerItem) => (a.rank ?? 9999) - (b.rank ?? 9999);

export function nextStepFor(program: string, enablers: readonly EnablerItem[]): EnablerItem | null {
  return [...enablers].sort(byRank).find((e) => isOpenStep(e) && (e.unlocks.programs[program]?.length ?? 0) > 0) ?? null;
}

export function coverageParts(cov: CoverageProgram, enablers: readonly EnablerItem[]): CoverageParts {
  const total = cov.objectives_in_scope;
  const seen = Math.min(cov.connected, total);
  const noSource = Math.min(cov.manual_only, total - seen);
  const reachable = Math.max(0, total - seen - noSource); // = the automatable gaps
  const step = nextStepFor(cov.program, enablers);
  const count = step ? Math.min(step.unlocks.programs[cov.program]!.length, reachable) : 0;
  return {
    program: cov.program, label: cov.label, total, seen, togo: total - seen,
    next: step && count > 0 ? { step, count } : null,
    other: reachable - count, noSource,
    preview: cov.preview_only ?? 0, partial: cov.partial ?? 0,
  };
}

// ---------------------------------------------------------------------------------------------------
// Trend: the card delta and the popover pace are ONE value
// ---------------------------------------------------------------------------------------------------

const dayMs = 86_400_000;
const dayOf = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

/** Seen now minus seen on the newest snapshot at least 7 days older; null with less than a week of history. */
export function weeklyDelta(cov: Pick<CoverageProgram, 'connected' | 'trend'>, today: string): number | null {
  const t0 = dayOf(today);
  const old = (cov.trend ?? []).filter((p) => dayOf(p.snapshot_date) <= t0 - 7 * dayMs).sort((a, b) => dayOf(b.snapshot_date) - dayOf(a.snapshot_date))[0];
  return old ? cov.connected - old.connected : null;
}

export function deltaText(delta: number | null): string | null {
  if (delta === null) return null;
  if (delta === 0) return 'No change this week';
  return delta > 0 ? `+${delta} this week` : `${delta} this week`;
}

// ---------------------------------------------------------------------------------------------------
// Status pill + popover, projection
// ---------------------------------------------------------------------------------------------------

export type ProgramStatusKey = 'on_track' | 'at_risk' | 'off_track' | 'not_started';
export const STATUS_WORD: Record<ProgramStatusKey, string> = { on_track: 'On track', at_risk: 'At risk', off_track: 'Off track', not_started: 'Not started' };

export interface ProgramStatusInput { status: ProgramStatusKey; date: string | null; dateWhat: string | null }

const SHORT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
export const shortDate = (iso: string) => SHORT.format(new Date(`${iso.slice(0, 10)}T12:00:00Z`));

/** "On track · Dec 1" — one time unit (a date), never "in 7 weeks". */
export function pillText(s: ProgramStatusInput): string {
  return s.date ? `${STATUS_WORD[s.status]} · ${shortDate(s.date)}` : STATUS_WORD[s.status];
}

export interface StatusLines { what: string; rule: string; seen: string }

/** The popover's three lines: what the date is, the pace rule, what "seen" means. */
export function statusLines(parts: CoverageParts, s: ProgramStatusInput, delta: number | null, today: string): StatusLines {
  const what = s.date ? `${s.dateWhat ?? 'Next date'} · ${shortDate(s.date)}.` : 'No date is set for this program yet.';
  let rule: string;
  const reach = parts.total - parts.noSource;
  if (delta !== null && s.date) {
    const weeks = Math.max(0, Math.floor((dayOf(s.date) - dayOf(today)) / (7 * dayMs)));
    const projected = Math.min(reach, parts.seen + Math.max(0, delta) * weeks);
    rule = `At the recent pace (${delta >= 0 ? '+' : ''}${delta} a week), ${projected} of ${parts.total} seen by ${shortDate(s.date)}.`;
  } else {
    rule = "We'll show a pace after a week of history.";
  }
  if (parts.noSource) rule += ` The other ${parts.noSource} need evidence you add.`;
  return { what, rule, seen: "An automated source reports on it. Seen isn't the same as passing." };
}

/** "After AWS: 25 of 56" (+ "· still at risk" when the program is not on track and work remains after the step). */
export function projection(parts: CoverageParts, status: ProgramStatusKey): { text: string; stillAtRisk: boolean; still: string } | null {
  if (!parts.next) return null;
  const after = parts.seen + parts.next.count;
  const stillAtRisk = (status === 'at_risk' || status === 'off_track') && (parts.other > 0 || parts.noSource > 0);
  return { text: `After ${shortStepName(parts.next.step)}: ${after} of ${parts.total}`, stillAtRisk, still: `still ${STATUS_WORD[status].toLowerCase()}` };
}

/** "Connect AWS production" → "AWS"; anything else keeps its title. */
export function shortStepName(e: Pick<EnablerItem, 'title' | 'code'>): string {
  const m = /^Connect (.+?)(?: production)?$/.exec(e.title);
  return m ? m[1]! : e.title.replace(/^Add the /, 'the ').replace(/^Send an? /, 'the ');
}

// ---------------------------------------------------------------------------------------------------
// The step list: groups + filter
// ---------------------------------------------------------------------------------------------------

// V112-3-3 (BL-39): 'attention' = a done step whose evidence stopped counting (a done CI gate step receiving signed runs
// that don't count). It leads the list instead of sitting under Done with a badge.
export type StepGroup = 'attention' | 'open' | 'progress' | 'done' | 'snoozed' | 'declined';
export const GROUP_LABEL: Record<StepGroup, string> = { attention: 'Needs attention', open: 'Open', progress: 'In progress', done: 'Done', snoozed: 'Snoozed', declined: 'Declined' };
export const GROUP_ORDER: readonly StepGroup[] = ['attention', 'open', 'progress', 'done', 'snoozed', 'declined'];

export function groupOf(e: EnablerItem): StepGroup {
  if (isUntrustedCiRun(e)) return 'attention';
  if (e.state === 'done' && e.progress.state !== 'partial') return 'done';
  if (e.state === 'declined') return 'declined';
  if (e.state === 'snoozed') return 'snoozed';
  if (e.state === 'in_progress' || ['waiting', 'failed', 'partial', 'needs_fix', 'not_counted'].includes(e.progress.state)) return 'progress';
  return 'open';
}

/**
 * V108-8-8: the step unlocks no objective because no program objective uses its evidence (the server names
 * the programs in `unlocks.not_taken_by`). Such steps sort last in their group, stay out of "Then" and the
 * coding-agent list, and say why once (NotTakenLine).
 */
export const movesNothing = (e: Pick<EnablerItem, 'objectives_unlocked' | 'unlocks'>) => e.objectives_unlocked === 0 && (e.unlocks.not_taken_by?.length ?? 0) > 0;

export function groupSteps(enablers: readonly EnablerItem[], program: string | null): { groups: Record<StepGroup, EnablerItem[]>; shown: number; total: number } {
  const groups: Record<StepGroup, EnablerItem[]> = { attention: [], open: [], progress: [], done: [], snoozed: [], declined: [] };
  let shown = 0;
  const last = (e: EnablerItem) => (e.state === 'blocked' ? 2 : 0) + (movesNothing(e) ? 1 : 0);
  for (const e of [...enablers].sort((a, b) => last(a) - last(b) || byRank(a, b))) {
    if (program && !(e.unlocks.programs[program]?.length)) continue;
    groups[groupOf(e)].push(e);
    shown++;
  }
  return { groups, shown, total: enablers.length };
}

/** Repo steps a coding agent can open pull requests for (the agent box count). */
// V108-8-13: a CI gate already sending runs (ci_trust) needs a person's trust, not another pull request.
export const agentSteps = (enablers: readonly EnablerItem[]) => enablers.filter((e) => e.actor === 'repo_agent' && isOpenStep(e) && !movesNothing(e) && !e.progress.ci_trust);

// ---------------------------------------------------------------------------------------------------
// Who: the viewer, approvers, the picker
// ---------------------------------------------------------------------------------------------------

export interface Person { user_id: string; name: string; role: string }

/** Console and collector steps need someone who can connect sources (owner / admin); repo steps anyone. */
export function canDo(person: Pick<Person, 'role'>, step: Pick<EnablerItem, 'actor'>): boolean {
  return step.actor === 'repo_agent' || person.role === 'owner' || person.role === 'admin';
}

/** "You" for the viewer, never their own name. */
export const personLabel = (userId: string | null, viewerId: string, people: readonly Person[]) =>
  !userId ? null : userId === viewerId ? 'You' : people.find((p) => p.user_id === userId)?.name ?? 'A teammate';

/** Picker options: people who can do the step, the viewer excluded, the suggested one first. */
export function pickerPeople(step: Pick<EnablerItem, 'actor' | 'assignee_user_id'>, people: readonly Person[], viewerId: string): Person[] {
  return people.filter((p) => p.user_id !== viewerId && canDo(p, step))
    .sort((a, b) => (a.user_id === step.assignee_user_id ? -1 : b.user_id === step.assignee_user_id ? 1 : 0) || a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------------------------------
// The next-step decision (coverage view "Do this next" + Today's strip). AC-1, AC-4.
// ---------------------------------------------------------------------------------------------------

/**
 * `fix`: a "Couldn't check" fix whose source still collects (progress `needs_fix`, V109-1-19 B3).
 * `trust` (V108-8-13): a CI gate step whose runs arrive but don't count yet, or that was just trusted (`ci_trust`).
 */
export type InFlightKind = 'failed' | 'fix' | 'trust' | 'waiting' | 'partial' | 'asked';

/** V108-8-13: the CI gate step's one action is "Trust it", for an owner / admin (the strip's primary). */
// Review M-1: only for an account the workspace connected; a foreign or look-alike account is a Review, never the primary.
export const needsTrust = (e: Pick<EnablerItem, 'progress'>) => e.progress.ci_trust?.status === 'untrusted' && e.progress.ci_trust.owner_known !== false;

/** A "Couldn't check" fix step (`fix:*`, V109-1-19). */
export const isFixStep = (e: Pick<EnablerItem, 'code'>) => e.code.startsWith('fix:');

export interface InFlight { step: EnablerItem; kind: InFlightKind; cause: FailureCauseView | null; owner: string | null }

/** A step somebody has started: a read is waiting / failed / partial, or it is sent to someone. */
export function inFlightOf(e: EnablerItem, slug: string): InFlight | null {
  // V112-3-3 (BL-39): a done CI gate step whose new runs don't count stays on Today until it's trusted or dismissed.
  if (!isOpenStep(e) && !(e.state === 'done' && e.progress.state === 'partial') && !isUntrustedCiRun(e)) return null;
  const p = e.progress;
  const cause = causeOf(e, slug);
  if (p.ci_trust && (e.state !== 'done' || isUntrustedCiRun(e))) return { step: e, kind: 'trust', cause: null, owner: e.assignee_user_id };
  if (p.state === 'failed') return { step: e, kind: 'failed', cause, owner: e.assignee_user_id };
  if (p.state === 'needs_fix' || (isFixStep(e) && (p.state === 'waiting' || p.state === 'not_started'))) return { step: e, kind: 'fix', cause: null, owner: e.assignee_user_id };
  if (p.state === 'partial') return { step: e, kind: 'partial', cause: null, owner: e.assignee_user_id };
  if (p.state === 'waiting' && (!p.needs_console || p.consent_started_at)) return { step: e, kind: 'waiting', cause: null, owner: e.assignee_user_id };
  if (e.assignee_user_id) return { step: e, kind: 'asked', cause: null, owner: e.assignee_user_id };
  return null;
}

/** The context failureCauseView needs (the one link per cause, AC-5). */
export function causeCtx(e: EnablerItem, slug: string) {
  const p = e.progress;
  const f = p.fix_context;
  return {
    slug, kind: p.kind ?? e.code.replace(/^connect:/, ''), connector_id: p.connector_id,
    role_arn: f?.role_arn ?? null, account_id: f?.account_id ?? null, external_id: f?.external_id ?? null, host_project_id: f?.host_project_id ?? null,
    target: p.target_label,
  };
}

/** The ONE view of a failed step's cause (AC-5): every screen calls this, never builds its own link. */
export function causeOf(e: EnablerItem, slug: string): FailureCauseView | null {
  return e.progress.state === 'failed' && e.progress.cause ? failureCauseView(e.progress.cause.code, causeCtx(e, slug)) : null;
}

export interface StripModel {
  /** The step already moving (shown first, with its live status). */
  status: InFlight | null;
  /**
   * The other steps that must stay visible (build review, flow B1/H4): every other FAILED step (each with
   * its own fix link, so a second failure never hides the first) and every step sent to the viewer.
   */
  others: InFlight[];
  /** The next new step (not in flight). */
  next: EnablerItem | null;
  /** Who gets the strip's one filled primary: the status row's fix, the next step, or nobody. */
  primary: 'status' | 'next' | null;
  /** The viewer can connect sources (owner / admin). */
  approver: boolean;
}

/**
 * State before role (AC-4): a step in flight is shown with its live status, never offered as a fresh
 * ask. AC-1: an approver's FAILED step takes the primary with its cause's fix action, over new work;
 * a WAITING step's "Check now" / "Open … again" are secondaries.
 */
export function stripModel(enablers: readonly EnablerItem[], viewer: { user_id: string; role: string }, slug: string): StripModel {
  const approver = viewer.role === 'owner' || viewer.role === 'admin';
  const sorted = [...enablers].sort(byRank);
  const flights = sorted.map((e) => inFlightOf(e, slug)).filter((x): x is InFlight => !!x);
  // V109-1-19 (H3): a "Couldn't check" fix leads (a broken source outranks setup work), then failed, then
  // waiting / partial, then asked; within a kind the step that unlocks the most first, then rank — the same
  // order the coverage bar uses for its "next" segment (build review flow H1: one ranking).
  const order: InFlightKind[] = ['failed', 'fix', 'trust', 'waiting', 'partial', 'asked'];
  const fixFirst = (f: InFlight) => (isFixStep(f.step) && (f.kind === 'failed' || f.kind === 'fix') ? 0 : 1);
  const ranked = flights.sort((a, b) => fixFirst(a) - fixFirst(b) || order.indexOf(a.kind) - order.indexOf(b.kind) || b.step.objectives_unlocked - a.step.objectives_unlocked || byRank(a.step, b.step));
  const status = ranked[0] ?? null;
  const others = ranked.slice(1).filter((f) => f.kind !== 'asked' || (f.owner === viewer.user_id && f.owner !== null));
  const inFlightIds = new Set(flights.map((f) => f.step.id));
  const nextRaw = sorted.find((e) => e.state === 'suggested' && !inFlightIds.has(e.id) && e.privilege.level === 'read_only' && e.objectives_unlocked > 0) ?? null;
  // V108-8-19: the step that makes it count comes first (Classify <system> before that system's CI gate), and a system
  // whose CI gate is already running or done (it checks no AI duty until classified) is classified first too (flow review).
  const gateLive = (c: EnablerItem) => c.code.startsWith('ci-gate:') && (inFlightIds.has(c.id) || c.state === 'done' || c.state === 'in_progress');
  const classifyForLiveGate = sorted.find((x) => isBlockingClassify(x, inFlightIds) && sorted.some((c) => c.subject_key === x.subject_key && gateLive(c))) ?? null;
  const next = classifyForLiveGate ?? (nextRaw ? blockingStepFor(nextRaw, sorted, inFlightIds) : null);
  const fixable = !!status && (((status.kind === 'failed' || status.kind === 'fix') && (approver || status.owner === viewer.user_id) && canDo({ role: approver ? 'owner' : viewer.role }, status.step))
    // V108-8-13: "Trust it" is owner / admin only (requireRole on the route), whoever the step was sent to.
    || (status.kind === 'trust' && approver && needsTrust(status.step)));
  const primary: StripModel['primary'] = fixable ? 'status' : next ? 'next' : null;
  return { status, others, next, primary, approver };
}

/**
 * V108-8-19: an open step that has to come first for this one to be worth what it unlocks: the "Classify <system>"
 * step for that system's CI gate (until then the gate runs a baseline policy with no AI-duty checks). Else the step.
 */
export function blockingStepFor(e: EnablerItem, list: readonly EnablerItem[], inFlight: ReadonlySet<string> = new Set()): EnablerItem {
  if (!e.code.startsWith('ci-gate:')) return e;
  return list.find((x) => x.subject_key === e.subject_key && isBlockingClassify(x, inFlight)) ?? e;
}

/**
 * An open Classify step for a system never classified. Answers that matched no rule don't block anything (flow /
 * glance review High): the CI gate keeps its place, and the review stays a Setup row.
 */
function isBlockingClassify(x: EnablerItem, inFlight: ReadonlySet<string>): boolean {
  const g = x.progress.gap;
  return x.code === 'classify:system' && x.state === 'suggested' && !inFlight.has(x.id) && g?.kind === 'system_classify' && g.reason === 'unclassified';
}

// ---------------------------------------------------------------------------------------------------
// Today: exactly one filled primary on the page
// ---------------------------------------------------------------------------------------------------

export type PrimaryHolder = 'cmmc' | 'objective' | 'miss' | 'other' | 'strip' | 'gov' | 'next' | null;

/**
 * One filled primary per surface (ADR-0071). Order: a red CMMC deadline row; the setup strip's fix for a
 * failed step (AC-1 — a broken source outranks routine work, build review glance B1); the GovRAMP card's
 * nearest-date action; a projected hard-date miss; a section that always leads with its own primary (the
 * signature queue's head, the solo advisor card); the setup strip's next step; then the item the viewer owes.
 */
export function todayPrimary(o: { cmmcRed: boolean; objectiveRed?: boolean; miss: boolean; other?: boolean; strip: StripModel['primary']; gov: boolean; next: boolean }): PrimaryHolder {
  if (o.cmmcRed) return 'cmmc';
  // V106-5-6 build review (glance H): a certificate that can be suspended (ISO surveillance late) outranks setup work.
  if (o.objectiveRed) return 'objective';
  if (o.strip === 'status') return 'strip';
  if (o.gov) return 'gov';
  if (o.miss && o.next) return 'miss';
  if (o.other) return 'other';
  if (o.strip === 'next') return 'strip';
  if (o.next) return 'next';
  return null;
}

/** "Check now" result, from the REFRESHED step (never the state captured before the check). */
export function checkResult(e: EnablerItem | undefined, slug: string): string {
  if (!e) return 'Checked. The status is up to date.';
  const vendor = e.progress.kind ? vendorName(e.progress.kind) : 'The source';
  if (e.progress.state === 'done' || e.state === 'done') return `Connected. First read${e.progress.target_label ? ` from ${e.progress.target_label}` : ''}.`;
  const cause = causeOf(e, slug);
  if (cause) return `Still needs attention: ${cause.line}`;
  if (e.progress.state === 'partial' && e.progress.accounts) return `${e.progress.accounts.read.length} of ${e.progress.accounts.read.length + e.progress.accounts.unread.length} accounts read.`;
  return `No read yet. ${vendor} can take a minute after you approve.`;
}

/** The stripe's label: the program's next step, with its state when it is already moving (flow H5). */
export function nextStepLabel(e: EnablerItem): string {
  const s = e.progress.state;
  const t = e.progress.ci_trust?.status;
  if (t) return t === 'trusted' ? `${e.title} · trusted, waiting for a run` : t === 'pull_request' ? `${e.title} · waiting for a merge` : `${e.title} · doesn't count yet`;
  return s === 'failed' ? `${e.title} · needs attention` : s === 'needs_fix' ? `${e.title} · couldn't check` : s === 'waiting' && (!e.progress.needs_console || e.progress.consent_started_at) ? `${e.title} · waiting` : s === 'partial' ? `${e.title} · partly read` : e.title;
}

// ---------------------------------------------------------------------------------------------------
// Warnings (V108-6-6)
// ---------------------------------------------------------------------------------------------------

export type WarningKind = 'privilege' | 'partial_scan' | 'staging' | 'stale';
export interface SetupWarning { kind: WarningKind; key: string; title: string; body: string; action: { label: string; href: string } }

// ---------------------------------------------------------------------------------------------------
// V108-8-2 / V108-8-3 (staging review 2026-10-13)
// ---------------------------------------------------------------------------------------------------

/** One entry of the Setup "Sources" line. */
export interface SourceItem {
  key: string;
  name: string;
  tone: 'done' | 'partial' | 'muted' | 'attention' | 'progress';
  /** What follows the name ("· 1 of 3 accounts", "· preview only", …), or null. */
  detail: string | null;
  /**
   * V108-8-13 (journey audit): where a source that doesn't count yet is fixed — its setup step, or the source's own
   * row in Manage sources (Reconnect / Read now / its environment). Absent when it counts.
   */
  action?: { label: string; href: string } | null;
  /** V108-8-18 (c): a preview (non-production) connection: the environment control renders right here. */
  preview?: { connector_id: string; kind: string | null; environment: string } | null;
}

/** The connector fields the Sources line reads (a row of GET /connectors). */
export interface SourceConnector { connector_id: string; kind: string; status: string; last_run_at?: string | null; needs_reconnect?: boolean; config_json?: string; environment?: string | null }

/**
 * V108-8-2: what is connected. Setup steps (enablers) that are done, partial or preview come first, as before; then
 * every other live connector from the connectors list (a GitHub App connection has no connect step, so it was
 * missing and the line read "Nothing connected yet"). A connector whose step is still open shows in the steps,
 * not here.
 */
export function sourcesLine(list: readonly EnablerItem[], connectors: readonly SourceConnector[], approver = true): SourceItem[] {
  const fromSteps = list.filter((e) => e.code.startsWith('connect:') || e.code.startsWith('grant:') || e.code.startsWith('collector:'))
    .filter((e) => e.state === 'done' || e.progress.state === 'partial' || (e.progress.connector_id && e.progress.environment && e.progress.environment !== 'production'));
  const items: SourceItem[] = fromSteps.map((e) => {
    const name = e.title.replace(/^Connect /, '');
    if (e.progress.state === 'partial' && e.progress.accounts) {
      const n = e.progress.accounts.read.length + e.progress.accounts.unread.length;
      return { key: e.id, name, tone: 'partial', detail: `${e.progress.accounts.read.length} of ${n} accounts` };
    }
    // Aesthetic M (V108-8-18 review): the environment control lives on the step's own row; here, a link to it.
    if (e.state !== 'done') return { key: e.id, name, tone: 'muted', detail: 'preview only', action: { label: 'Is it production?', href: `?step=${encodeURIComponent(e.id)}#steps` } };
    return { key: e.id, name, tone: 'done', detail: null };
  });
  const byStep = new Set(list.map((e) => e.progress.connector_id).filter((id): id is string => !!id));
  // A kind with a setup step speaks for its connections through that step (Sources or the steps list); only kinds
  // without one (a GitHub App connection) are added here, so no source shows two different states.
  const kindOf = (e: EnablerItem) => e.progress.kind ?? (e.code.startsWith('connect:') ? e.code.slice(8) : null);
  const byKind = new Set(list.map(kindOf).filter((k): k is string => !!k));
  for (const c of connectors) {
    if (byStep.has(c.connector_id) || byKind.has(c.kind)) continue;
    if (c.status !== 'active' && !c.needs_reconnect) continue;
    let target = '';
    if (c.kind === 'github') {
      try { const cfg = JSON.parse(c.config_json ?? '{}') as { owner?: string; repo?: string }; target = cfg.owner && cfg.repo ? ` · ${cfg.owner}/${cfg.repo}` : ''; } catch { target = ''; }
    }
    const name = `${vendorName(c.kind)}${target}`;
    // V108-8-13 (journey audit): a source with no setup step that doesn't count yet links to its own row.
    const manage = { label: `Open ${vendorName(c.kind)} in Manage sources`, href: '#manage-sources' };
    if (c.needs_reconnect) items.push({ key: c.connector_id, name, tone: 'attention', detail: 'sign-in expired', action: { ...manage, label: `Reconnect ${vendorName(c.kind)}` } });
    else if (c.environment && c.environment !== 'production') items.push({ key: c.connector_id, name, tone: 'muted', detail: 'preview only', action: manage, preview: { connector_id: c.connector_id, kind: c.kind, environment: c.environment } });
    else if (!c.last_run_at) items.push({ key: c.connector_id, name, tone: 'progress', detail: 'first read pending', action: { ...manage, label: `Read ${vendorName(c.kind)} now` } });
    else items.push({ key: c.connector_id, name, tone: 'done', detail: 'connected' });
  }
  // V108-8-13: CI that sends signed runs GAC Pro can't count yet, with the step that fixes it (Trust it).
  // V108-8-20: a DONE CI gate step that receives new signed runs nobody trusts (a new, renamed or moved workflow or
  // branch) is named too, as attention: its earlier runs still count, the new ones don't.
  const ciSeen = new Set<string>();
  for (const e of list) {
    const t = e.progress.ci_trust;
    if (!t || !t.repository || ciSeen.has(t.repository.toLowerCase())) continue;
    if (e.state === 'done' && !isNewCiRun(e)) continue;
    ciSeen.add(t.repository.toLowerCase());
    const href = stepHref(e.id);
    const name = `GitHub Actions · ${t.repository}`;
    if (t.status === 'untrusted') items.push({ key: `ci-${e.id}`, name, tone: 'attention', detail: e.state === 'done' ? "new signed runs don't count yet" : 'signed runs not trusted yet',
      // Flow review (Medium): the label promises only what the viewer can do (others ask an owner / admin there).
      action: { label: approver ? 'Trust it in the step' : 'Ask an admin in the step', href } });
    else if (t.status === 'pull_request') items.push({ key: `ci-${e.id}`, name, tone: e.state === 'done' ? 'progress' : 'muted',
      detail: e.state === 'done' ? 'new pull-request runs · waiting for a merge' : 'pull-request runs only', action: { label: 'See the step', href } });
    else if (t.status === 'trusted') items.push({ key: `ci-${e.id}`, name, tone: 'progress', detail: 'trusted · next run counts', action: { label: 'See the step', href } });
  }
  // Flow review: CI results that arrive unverified (no observed workflow) are named too, never silently absent.
  // V108-8-20: one per system, named, linked to THAT system's step; unsigned runs point to signing, not to trust.
  for (const e of list) {
    const t = e.progress.ci_trust;
    if (t?.status !== 'not_counted' || (e.state === 'done' && !isNewCiRun(e))) continue;
    const system = ciSystemName(e);
    const name = system ? `CI results · ${system}` : 'CI results';
    const fresh = e.state === 'done' ? 'new ' : '';
    items.push(t.unsigned
      ? { key: `ci-${e.id}`, name, tone: 'attention', detail: `${fresh}unsigned runs`, action: { label: 'Set up signing in the step', href: stepHref(e.id) } }
      : { key: `ci-${e.id}`, name, tone: 'attention', detail: e.state === 'done' ? 'new runs not signed by a trusted workflow' : 'not signed by a trusted workflow', action: { label: 'See the step', href: stepHref(e.id) } });
  }
  return items;
}

/** A link to one step on the Setup page (in-page: EnablerList opens, scrolls to and focuses it on every click). */
export const stepHref = (id: string) => `?step=${encodeURIComponent(id)}#steps`;

/** V108-8-20: a done CI gate step whose NEW runs don't count (untrusted, pull-request only, unsigned): non-blocking attention. */
export const isNewCiRun = (e: Pick<EnablerItem, 'state' | 'progress'>) => {
  const t = e.progress.ci_trust;
  return e.state === 'done' && !!t?.step_done && t.status !== 'trusted';
};

/**
 * V112-3-3 (BL-39): the part of isNewCiRun a person can resolve — runs from an untrusted workflow (Trust it, Not
 * ours, Don't count this branch). Only this moves to Needs attention and Today; pull-request-only or unsigned runs
 * have no dismiss, so they stay in Done with the explanation instead of becoming a permanent alarm.
 */
export const isUntrustedCiRun = (e: Pick<EnablerItem, 'state' | 'progress'>) => isNewCiRun(e) && e.progress.ci_trust?.status === 'untrusted';

/** The system a CI gate step is about (the server's `subject_label`), or null when an older Worker doesn't say. */
export const ciSystemName = (e: Pick<EnablerItem, 'code' | 'subject_label'>): string | null => (e.code.startsWith('ci-gate:') ? e.subject_label ?? null : null);

/**
 * V108-8-3: a positive pill needs something behind it. With nothing seen and under a week of history, an
 * "On track" program reads "Not started" (neutral). At risk / off track are kept: those come from dates.
 */
export function coverageStatus(s: ProgramStatusInput, cov: Pick<CoverageProgram, 'connected' | 'trend'> | null | undefined, today: string): ProgramStatusInput {
  if (!cov || s.status !== 'on_track' || cov.connected > 0 || weeklyDelta(cov, today) !== null) return s;
  return { ...s, status: 'not_started' };
}
